import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import DataTable from '../../components/ui/data-table';
import EmptyState from '../../components/ui/empty-state.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { academicService } from '../../services/academicService';
import { BookOpen, Users, Calendar, MapPin, GraduationCap } from 'lucide-react';

const CoursesPage = () => {
  const [subjects, setSubjects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await academicService.getMyClasses();
      console.log('Classes API Response:', res.data);
      
      // Extract subjects from classes
      const classes = res.data.data?.classes || res.data.data || [];
      const allSubjects = [];
      
      classes.forEach((classItem) => {
        if (classItem.classSubjects && Array.isArray(classItem.classSubjects) && classItem.classSubjects.length > 0) {
          classItem.classSubjects.forEach((classSubject) => {
            // Ensure classSubject and subject exist
            if (classSubject && classSubject.subject && classSubject.subject.name) {
              const subject = classSubject.subject;
              allSubjects.push({
                id: subject.id,
                name: subject.name,
                code: subject.code || null,
                description: subject.description || null,
                credits: subject.credits || null,
                isCore: subject.isCore || false,
                className: classItem.name || 'Unknown Class',
                classId: classItem.id,
                teacher: classSubject.teacher || null
              });
            }
          });
        }
      });
      
      console.log('Processed subjects:', allSubjects);
      setSubjects(allSubjects);
    } catch (e) {
      console.error('Subjects fetch error:', e);
      setError(e.response?.data?.message || 'Failed to load subjects');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchSubjects();
  }, []);

  const columns = [
    { 
      id: 'name', 
      header: 'Subject', 
      accessorKey: 'name', 
      sortable: true,
      cell: ({ row }) => {
        // In this DataTable, row IS the data object itself, not row.original
        const subject = row;
        if (!subject) {
          return <span className="text-gray-400">Unknown Subject</span>;
        }
        const subjectName = subject.name;
        if (!subjectName) {
          console.warn('Subject missing name:', subject);
          return <span className="text-gray-400">Unknown Subject</span>;
        }
        return (
          <div className="flex items-center space-x-2">
            <BookOpen className="h-4 w-4 text-blue-600" />
            <div>
              <div className="font-medium">{subjectName}</div>
              {subject.code && (
                <div className="text-sm text-gray-500">{subject.code}</div>
              )}
            </div>
          </div>
        );
      }
    },
    { 
      id: 'class', 
      header: 'Class', 
      accessorKey: 'className',
      cell: ({ row }) => {
        const className = row?.className;
        return <Badge variant="outline">{className || 'N/A'}</Badge>;
      }
    },
    { 
      id: 'teacher', 
      header: 'Teacher', 
      accessorKey: 'teacher',
      cell: ({ row }) => {
        const teacher = row?.teacher;
        if (!teacher || !teacher.firstName) {
          return <span className="text-gray-400">Not assigned</span>;
        }
        return (
          <div className="flex items-center space-x-2">
            <GraduationCap className="h-4 w-4 text-green-600" />
            <div>
              <div className="font-medium">{teacher.firstName} {teacher.lastName || ''}</div>
              {teacher.email && (
                <div className="text-sm text-gray-500">{teacher.email}</div>
              )}
            </div>
          </div>
        );
      }
    },
    { 
      id: 'description', 
      header: 'Description', 
      accessorKey: 'description',
      cell: ({ row }) => {
        const description = row?.description;
        return (
          <p className="text-sm text-gray-600 max-w-md truncate">
            {description || 'No description'}
          </p>
        );
      }
    }
  ];

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <div className="text-red-600 text-sm mb-4">{String(error)}</div>
            <Button onClick={fetchSubjects}>Retry</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">My Subjects</h2>
          <p className="text-gray-600 mt-1">View your enrolled subjects and class information</p>
        </div>
        <Button onClick={fetchSubjects} variant="outline">
          <Calendar className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {(!subjects || subjects.length === 0) ? (
        <EmptyState 
          icon={BookOpen}
          title="No subjects found"
          description="You are not enrolled in any subjects yet. Contact your administrator if you believe this is an error."
          action
          actionLabel="Refresh"
          onAction={fetchSubjects}
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BookOpen className="h-5 w-5" />
              <span>Enrolled Subjects ({subjects.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <DataTable 
              data={subjects} 
              columns={columns} 
              searchable 
              searchPlaceholder="Search subjects..."
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CoursesPage;
